/**
 * PricingContext - State management for Pricing Analysis feature
 *
 * Manages price elasticity calculations, optimal price points,
 * demand curve fitting, and price simulation scenarios.
 */

import * as React from 'react';
import type { PricingDataPoint, PromotionType } from '@/shared/types';
import type {
  PriceElasticityResult,
  ElasticityInterpretation,
} from '@/workers/statisticsWorker.types';

// Re-export worker types for consumers
export type { PriceElasticityResult, ElasticityInterpretation };

// =============================================================================
// Types
// =============================================================================

/**
 * Optimal price point recommendation
 */
export interface OptimalPricePoint {
  /** SKU identifier */
  sku: string;
  /** Optimization target used */
  optimizationTarget: OptimizationTarget;
  /** Recommended optimal price */
  optimalPrice: number;
  /** Current average price */
  currentPrice: number;
  /** Price change percentage */
  priceChangePercent: number;
  /** Projected metric improvement */
  projectedImprovement: {
    metric: 'revenue' | 'gross_margin' | 'net_margin' | 'units';
    currentValue: number;
    projectedValue: number;
    percentChange: number;
  };
  /** Confidence in the recommendation */
  confidenceScore: 'high' | 'medium' | 'low';
}

/**
 * Demand curve model types
 */
export type DemandCurveModel = 'linear' | 'log_linear' | 'exponential' | 'quadratic';

/**
 * Fitted demand curve for a SKU
 */
export interface DemandCurve {
  /** SKU identifier */
  sku: string;
  /** Model type used */
  modelType: DemandCurveModel;
  /** Model coefficients */
  coefficients: {
    a: number;  // Intercept
    b: number;  // Price coefficient
    c?: number; // Quadratic term (if applicable)
  };
  /** R-squared fit quality */
  r2: number;
  /** Root mean square error */
  rmse: number;
  /** AIC for model comparison */
  aic: number;
  /** Price range where model is valid */
  validPriceRange: {
    min: number;
    max: number;
  };
}

/**
 * Optimization target for price recommendations
 */
export type OptimizationTarget =
  | 'maximize_revenue'
  | 'maximize_gross_margin'
  | 'maximize_net_margin'
  | 'maximize_units';

/**
 * Price simulation input parameters
 */
export interface SimulationInputs {
  /** SKU to simulate */
  sku: string;
  /** New price to test */
  testPrice: number;
  /** Cost assumptions */
  costs: {
    cogs: number;
    fbaFee: number;
    referralFee: number;
    shippingCost: number;
  };
  /** Optional promotion to apply */
  promotion?: {
    type: PromotionType;
    discountPercent: number;
  };
  /** Competitor price context */
  competitorPrice?: number;
}

/**
 * Price simulation results
 */
export interface SimulationResults {
  /** SKU simulated */
  sku: string;
  /** Input parameters used */
  inputs: SimulationInputs;
  /** Projected outcomes */
  projections: {
    /** Estimated units per day */
    unitsPerDay: number;
    unitsPerDayCI: { lower: number; upper: number };
    /** Estimated daily revenue */
    dailyRevenue: number;
    dailyRevenueCI: { lower: number; upper: number };
    /** Gross margin per unit */
    grossMarginPerUnit: number;
    /** Gross margin percentage */
    grossMarginPercent: number;
    /** Net margin per unit */
    netMarginPerUnit: number;
    /** Net margin percentage */
    netMarginPercent: number;
    /** Total daily profit */
    dailyProfit: number;
    dailyProfitCI: { lower: number; upper: number };
  };
  /** Comparison to current state */
  comparison: {
    unitsChange: number;
    unitsChangePercent: number;
    revenueChange: number;
    revenueChangePercent: number;
    profitChange: number;
    profitChangePercent: number;
  };
  /** Model confidence */
  confidenceLevel: 'high' | 'medium' | 'low';
  /** Timestamp of simulation */
  simulatedAt: string;
}

/**
 * Date range filter for historical data
 */
export interface DateRange {
  /** Start date (ISO format) */
  startDate: string;
  /** End date (ISO format) */
  endDate: string;
}

// =============================================================================
// State Interface
// =============================================================================

export interface PricingState {
  // Raw data
  /** Historical pricing data by SKU */
  historicalPrices: Map<string, PricingDataPoint[]> | null;

  // Computed results
  /** Price elasticity calculations by SKU */
  priceElasticity: Map<string, PriceElasticityResult> | null;
  /** Optimal price recommendations by SKU */
  optimalPricePoints: Map<string, OptimalPricePoint> | null;
  /** Fitted demand curves by SKU */
  demandCurves: Map<string, DemandCurve> | null;

  // Simulation
  /** Current simulation inputs */
  simulationInputs: SimulationInputs | null;
  /** Current simulation results */
  simulationResults: SimulationResults | null;

  // UI state
  /** Currently selected SKU for detail view */
  selectedSku: string | null;
  /** Date range filter */
  dateRange: DateRange | null;
  /** Current optimization target */
  optimizationTarget: OptimizationTarget;

  // Loading and error states
  /** Whether data is loading */
  isLoading: boolean;
  /** Whether elasticity is being calculated */
  isCalculatingElasticity: boolean;
  /** Whether simulation is running */
  isSimulating: boolean;
  /** Error message */
  error: string | null;
  /** Success message */
  successMessage: string | null;

  // Data source info
  /** Uploaded file name */
  fileName: string;
}

// =============================================================================
// Actions
// =============================================================================

export type PricingAction =
  // Data loading
  | { type: 'PRICING_DATA_UPLOAD_START'; payload: { fileName: string } }
  | { type: 'PRICING_DATA_UPLOAD_SUCCESS'; payload: {
      historicalPrices: Map<string, PricingDataPoint[]>;
      successMessage: string;
    }}
  | { type: 'PRICING_DATA_UPLOAD_ERROR'; payload: string }

  // Elasticity calculations
  | { type: 'CALCULATE_ELASTICITY_START' }
  | { type: 'CALCULATE_ELASTICITY_SUCCESS'; payload: Map<string, PriceElasticityResult> }
  | { type: 'CALCULATE_ELASTICITY_ERROR'; payload: string }

  // Optimal price points
  | { type: 'SET_OPTIMAL_PRICE_POINTS'; payload: Map<string, OptimalPricePoint> }

  // Demand curves
  | { type: 'SET_DEMAND_CURVES'; payload: Map<string, DemandCurve> }

  // Simulation
  | { type: 'SET_SIMULATION_INPUTS'; payload: SimulationInputs }
  | { type: 'RUN_SIMULATION_START' }
  | { type: 'RUN_SIMULATION_SUCCESS'; payload: SimulationResults }
  | { type: 'RUN_SIMULATION_ERROR'; payload: string }
  | { type: 'CLEAR_SIMULATION' }

  // UI state
  | { type: 'SET_SELECTED_SKU'; payload: string | null }
  | { type: 'SET_DATE_RANGE'; payload: DateRange | null }
  | { type: 'SET_OPTIMIZATION_TARGET'; payload: OptimizationTarget }

  // General
  | { type: 'CLEAR_PRICING_ERROR' }
  | { type: 'CLEAR_PRICING_SUCCESS' }
  | { type: 'RESET_PRICING_STATE' };

// =============================================================================
// Initial State
// =============================================================================

const initialState: PricingState = {
  // Raw data
  historicalPrices: null,

  // Computed results
  priceElasticity: null,
  optimalPricePoints: null,
  demandCurves: null,

  // Simulation
  simulationInputs: null,
  simulationResults: null,

  // UI state
  selectedSku: null,
  dateRange: null,
  optimizationTarget: 'maximize_net_margin',

  // Loading and error states
  isLoading: false,
  isCalculatingElasticity: false,
  isSimulating: false,
  error: null,
  successMessage: null,

  // Data source info
  fileName: '',
};

// =============================================================================
// Reducer
// =============================================================================

function pricingReducer(state: PricingState, action: PricingAction): PricingState {
  switch (action.type) {
    // Data loading
    case 'PRICING_DATA_UPLOAD_START':
      return {
        ...state,
        isLoading: true,
        error: null,
        successMessage: null,
        fileName: action.payload.fileName,
        // Clear previous data on new upload
        historicalPrices: null,
        priceElasticity: null,
        optimalPricePoints: null,
        demandCurves: null,
        simulationInputs: null,
        simulationResults: null,
      };

    case 'PRICING_DATA_UPLOAD_SUCCESS':
      return {
        ...state,
        isLoading: false,
        historicalPrices: action.payload.historicalPrices,
        successMessage: action.payload.successMessage,
      };

    case 'PRICING_DATA_UPLOAD_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        historicalPrices: null,
        fileName: '',
      };

    // Elasticity calculations
    case 'CALCULATE_ELASTICITY_START':
      return {
        ...state,
        isCalculatingElasticity: true,
        error: null,
      };

    case 'CALCULATE_ELASTICITY_SUCCESS':
      return {
        ...state,
        isCalculatingElasticity: false,
        priceElasticity: action.payload,
      };

    case 'CALCULATE_ELASTICITY_ERROR':
      return {
        ...state,
        isCalculatingElasticity: false,
        error: action.payload,
      };

    // Optimal price points
    case 'SET_OPTIMAL_PRICE_POINTS':
      return {
        ...state,
        optimalPricePoints: action.payload,
      };

    // Demand curves
    case 'SET_DEMAND_CURVES':
      return {
        ...state,
        demandCurves: action.payload,
      };

    // Simulation
    case 'SET_SIMULATION_INPUTS':
      return {
        ...state,
        simulationInputs: action.payload,
        // Clear previous results when inputs change
        simulationResults: null,
      };

    case 'RUN_SIMULATION_START':
      return {
        ...state,
        isSimulating: true,
        error: null,
      };

    case 'RUN_SIMULATION_SUCCESS':
      return {
        ...state,
        isSimulating: false,
        simulationResults: action.payload,
      };

    case 'RUN_SIMULATION_ERROR':
      return {
        ...state,
        isSimulating: false,
        error: action.payload,
        simulationResults: null,
      };

    case 'CLEAR_SIMULATION':
      return {
        ...state,
        simulationInputs: null,
        simulationResults: null,
      };

    // UI state
    case 'SET_SELECTED_SKU':
      return {
        ...state,
        selectedSku: action.payload,
      };

    case 'SET_DATE_RANGE':
      return {
        ...state,
        dateRange: action.payload,
      };

    case 'SET_OPTIMIZATION_TARGET':
      return {
        ...state,
        optimizationTarget: action.payload,
        // Clear optimal prices when target changes (need recalculation)
        optimalPricePoints: null,
      };

    // General
    case 'CLEAR_PRICING_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'CLEAR_PRICING_SUCCESS':
      return {
        ...state,
        successMessage: null,
      };

    case 'RESET_PRICING_STATE':
      return { ...initialState };

    default:
      return state;
  }
}

// =============================================================================
// Context
// =============================================================================

interface PricingContextType {
  state: PricingState;
  dispatch: React.Dispatch<PricingAction>;
}

const PricingContext = React.createContext<PricingContextType | undefined>(undefined);

// =============================================================================
// Provider
// =============================================================================

export const PricingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = React.useReducer(pricingReducer, initialState);

  return (
    <PricingContext.Provider value={{ state, dispatch }}>
      {children}
    </PricingContext.Provider>
  );
};

// =============================================================================
// Hook
// =============================================================================

export const usePricingContext = () => {
  const context = React.useContext(PricingContext);
  if (context === undefined) {
    throw new Error('usePricingContext must be used within a PricingProvider');
  }
  return context;
};

// =============================================================================
// Selector Helpers (optional but useful)
// =============================================================================

/**
 * Get elasticity result for a specific SKU
 */
export const selectElasticityForSku = (
  state: PricingState,
  sku: string
): PriceElasticityResult | null => {
  return state.priceElasticity?.get(sku) ?? null;
};

/**
 * Get optimal price for a specific SKU
 */
export const selectOptimalPriceForSku = (
  state: PricingState,
  sku: string
): OptimalPricePoint | null => {
  return state.optimalPricePoints?.get(sku) ?? null;
};

/**
 * Get demand curve for a specific SKU
 */
export const selectDemandCurveForSku = (
  state: PricingState,
  sku: string
): DemandCurve | null => {
  return state.demandCurves?.get(sku) ?? null;
};

/**
 * Get historical prices for a specific SKU within date range
 */
export const selectFilteredPricesForSku = (
  state: PricingState,
  sku: string
): PricingDataPoint[] => {
  const prices = state.historicalPrices?.get(sku);
  if (!prices) return [];

  if (!state.dateRange) return prices;

  const { startDate, endDate } = state.dateRange;
  return prices.filter(p => p.date >= startDate && p.date <= endDate);
};

/**
 * Get count of SKUs with pricing data
 */
export const selectSkuCount = (state: PricingState): number => {
  return state.historicalPrices?.size ?? 0;
};

/**
 * Get list of all SKUs with pricing data
 */
export const selectAllSkus = (state: PricingState): string[] => {
  return state.historicalPrices ? Array.from(state.historicalPrices.keys()) : [];
};
